import { useState, useEffect } from "react";
import {
  Flex,
  View,
  Link,
  ToggleButton,
  ActionButton,
  MenuTrigger,
  Menu,
  Item,
} from "@adobe/react-spectrum";
import { Grid, Fullscreen, ThreeDots } from "react-bootstrap-icons";
import FilterSearchTextBox from "./FilterSearchTextBox";
import { useTranslation } from "react-i18next";
import { CopyIcon } from "../../../../components/protected/Icons/IconsLib";
import {
  EditIcon,
  TrashIcon,
} from "../../../../components/protected/Icons/IconsLib";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import styles from "./header_component.module.css";
interface IHeaderComponentProps {
  onPageViewChange: (view: string) => void;
  autoChanged: string;
  filterParams: any;
  onSearchChange: (params: {}) => void;
  fpaTypeCategory: string;
  onAddNew: () => void;
  onCopy?: () => void;
  onDelete: () => void;
  onEdit?: () => void;
}

const HeaderComponent: React.FC<IHeaderComponentProps> = ({
  onPageViewChange,
  autoChanged,
  filterParams,
  onSearchChange,
  fpaTypeCategory,
  onAddNew,
  onCopy,
  onDelete,
  onEdit,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useViewInfo();
  const [selectedButton, setSelectedButton] = useState<string>("Grid");
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setSelectedButton((prev) => (prev === autoChanged ? prev : autoChanged));
  }, [autoChanged]);

  const handleToggle = (view: string) => {
    setSelectedButton((prev) => (prev === view ? prev : view));
    onPageViewChange(view);
  };

  const handleCopy = () => {
    if (onCopy) {
      onCopy();
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
  };

  // Determine if we should show compact layout
  const isCompact = windowWidth < 768;
  const isSmallTablet = windowWidth >= 768 && windowWidth < 1024;
  const isMacBookAir = windowWidth >= 1024 && windowWidth <= 1440; // Mac Air range
  const showFullActions = windowWidth >= 1024;

  // Mobile Actions Menu Component
  const MobileActionsMenu = () => (
    <MenuTrigger>
      <ActionButton
        UNSAFE_className={styles.mobile_menu_button}
        aria-label="Actions"
      >
        <ThreeDots size={20} />
      </ActionButton>
      <Menu
        onAction={(key) => {
          switch (key) {
            case "edit":
              handleEdit();
              break;
            case "copy":
              handleCopy();
              break;
            case "delete":
              onDelete();
              break;
          }
        }}
      >
        <Item key="edit">
          <EditIcon size={16} />
          <span style={{ marginLeft: 8 }}>
            {t("edit", { ns: "layout_components" })}
          </span>
        </Item>
        <Item key="copy">
          <CopyIcon size={16} />
          <span style={{ marginLeft: 8 }}>
            {t("copy", { ns: "layout_components" })}
          </span>
        </Item>
        <Item key="delete">
          <TrashIcon size={16} />
          <span style={{ marginLeft: 8 }}>
            {t("delete", { ns: "layout_components" })}
          </span>
        </Item>
      </Menu>
    </MenuTrigger>
  );

  return (
    <Flex
      direction={isCompact ? "column" : "row"}
      justifyContent={"space-between"}
      gap={isCompact ? "size-150" : "size-0"}
      UNSAFE_className={styles.header_parent}
      wrap={isSmallTablet}
    >
      {/* Left Section - Add New and Search */}
      <Flex
        direction={"row"}
        gap={
          isCompact
            ? "size-100"
            : isSmallTablet || isMacBookAir
              ? "size-150"
              : "size-200"
        }
        alignItems={"center"}
        justifyContent={isCompact ? "space-between" : "start"}
        width={isCompact ? "100%" : "auto"}
        wrap={isSmallTablet || isMacBookAir}
      >
        <Link
          isQuiet
          onPress={(e) => {
            onAddNew();
          }}
          UNSAFE_className={`${styles.add_new_btn} ${isCompact ? styles.add_new_btn_compact : ""}`}
        >
          <i className="bi bi-plus fs-5">
            <View UNSAFE_className={styles.add_new_btn_text}>
              {isCompact
                ? t("add", { ns: "layout_components" })
                : t("add_new", { ns: "layout_components" })}
            </View>
          </i>
        </Link>

        <Flex direction={"column"} flex={isCompact ? 1 : "initial"}>
          <Flex direction={"row"} UNSAFE_className={styles.activity_search}>
            <FilterSearchTextBox
              onSearchChange={onSearchChange}
              filterParams={filterParams}
              fpaTypeCategory={fpaTypeCategory}
            />
            <i className={`${styles.icon_activity_search} bi bi-search`}></i>
          </Flex>
        </Flex>

        <View UNSAFE_className={styles.frame_divider}></View>
      </Flex>

      {/* Right Section - Actions and View Toggles */}
      <Flex
        direction={"row"}
        gap={
          isCompact
            ? "size-100"
            : isSmallTablet || isMacBookAir
              ? "size-75"
              : "size-100"
        }
        alignItems={"center"}
        justifyContent={isCompact ? "space-between" : "end"}
        width={isCompact ? "100%" : "auto"}
      >
        {/* Action Buttons */}
        {showFullActions ? (
          // Desktop - Show all action buttons
          <Flex
            direction={"row"}
            UNSAFE_className={styles.action_btn_container}
          >
            <div className={styles.action_btn} onClick={handleEdit}>
              <EditIcon size={isMacBookAir ? 28 : 32} />
            </div>
            <div className={styles.action_btn} onClick={() => onDelete()}>
              <TrashIcon size={isMacBookAir ? 28 : 32} />
            </div>
            <div className={styles.action_btn} onClick={handleCopy}>
              <CopyIcon size={isMacBookAir ? 28 : 32} />
            </div>
          </Flex>
        ) : isSmallTablet ? (
          // Tablet - Show limited actions
          <Flex
            direction={"row"}
            UNSAFE_className={styles.action_btn_container}
          >
            <div className={styles.action_btn} onClick={handleEdit}>
              <EditIcon size={28} />
            </div>
            <div className={styles.action_btn} onClick={() => onDelete()}>
              <TrashIcon size={28} />
            </div>
          </Flex>
        ) : (
          // Mobile - Show dropdown menu
          <MobileActionsMenu />
        )}

        {/* View Toggle Buttons */}
        <Flex direction={"row"} gap={"size-50"} alignItems={"center"}>
          <ToggleButton
            isSelected={selectedButton === "Grid"}
            onChange={() => handleToggle("Grid")}
            isEmphasized
            UNSAFE_className={styles.toggle_button}
          >
            <Grid size={isCompact ? 16 : isMacBookAir ? 18 : 20} />
          </ToggleButton>

          <ToggleButton
            isSelected={selectedButton === "Full"}
            onChange={() => handleToggle("Full")}
            isEmphasized
            UNSAFE_className={styles.toggle_button}
          >
            <Fullscreen size={isCompact ? 16 : isMacBookAir ? 18 : 20} />
          </ToggleButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default HeaderComponent;
